import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import "@styles/pages/EarnSDollar/step1/EarnSDollarForm.scss";
import {
  Box,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import dayjs from "dayjs";

import { EarnSDollarValidationSchema } from "@models/ValidationSchema";
import { Trans, useTranslation } from "react-i18next";
import { getChannel } from "@api/commonApi";
import { productRegistration } from "@api/sDollarApi";
import { setFormStep } from "@store/ui/formStepSlice";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";

const EarnSDollarForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [shopOptions, setShopOptions] = useState([]);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isChinese = i18n.language === "hk";
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(EarnSDollarValidationSchema),
    defaultValues: {},
  });

  useEffect(() => {
    const fetchChannel = async () => {
      try {
        const res = await getChannel();
        const data = res.data.Data;
        setShopOptions(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchChannel();
  }, []);
  const onError = (errs) => {
    const first = Object.keys(errs)[0];
    if (!first) return;

    // 先試原生 input/textarea/select（大多數欄位都覆蓋到）
    let el =
      document.querySelector(`input[name="${first}"]`) ||
      document.querySelector(`textarea[name="${first}"]`) ||
      document.querySelector(`select[name="${first}"]`);

    // 兼容 react-select（你有用 classNamePrefix）
    if (!el) {
      // 國碼下拉（countries）
      el = document.querySelector(".dropdown-class-countries__control");
    }
    if (!el && first === "purchaseChannel") {
      // 購買渠道下拉（channels）
      el = document.querySelector(".dropdown-class-channels__control");
    }

    // 兼容 datepicker（用你嘅 .datepicker class）
    if (!el && first === "purchaseDate") {
      const dp = document.querySelector(".datepicker");
      // 盡量抓到內層真正 input（react-datepicker 會有 input）
      el =
        dp?.querySelector("input") ||
        document.querySelector(`input[name="${first}"]`) ||
        dp;
    }

    // 滾到視窗中間（如有 fixed header，見下方 CSS）
    el?.scrollIntoView({ behavior: "smooth", block: "center" });

    // 原生欄位可直接 setFocus（非原生就算唔 focus，都已經滾到位）
    try {
      setFocus(first, { shouldSelect: true });
    } catch {}
  };
  const onSubmit = (data) => {
    const formData = new FormData();

    // ✅ append primitive fields
    formData.append("invoice_no", data.invoice_number);
    formData.append("purchase_price", data.purchase_price);
    formData.append("serial_no", data.serial_number);
    formData.append("channel_id", data.purchase_shop?.channel_id);
    // ✅ append formatted date
    if (data.purchase_date) {
      formData.append(
        "purchase_date",
        format(data.purchase_date, "yyyy/MM/dd")
      );
    }

    // ✅ append image file
    if (data.invoice_image) {
      formData.append("invoice_image", data.invoice_image);
    }
    if (data.invoice_image2) {
      formData.append("invoice_image2", data.invoice_image2);
    }

    // 📨 submit
    const postEarnSDollarForm = async () => {
      try {
        setIsLoading(true);
        const res = await productRegistration(formData); // 這裡要支援 FormData
        const statusCode = res.data.StatusCode;

        console.log("Form submitted! Status:", statusCode);
        switch (statusCode) {
          case 200:
            toast.success(
              t("status_message:status_code_200_product_registration")
            );
            dispatch(setFormStep({ formKey: "earnSDollar", step: 1 }));
            break;
          case 301:
            toast.error(t("status_message:status_code_301"));
            break;
          case 302:
            toast.error(t("status_message:status_code_302"));
            break;
          case 401:
            toast.error(t("status_message:status_code_401"));
            break;
          case 402:
            toast.error(t("status_message:status_code_402"));
            break;
          case 403:
            toast.error(t("status_message:status_code_403"));
            break;
          case 404:
            toast.error(t("status_message:status_code_404"));
            break;
          case 500:
            toast.error(t("status_message:status_code_500"));
            break;
        }
      } catch (err) {
        console.error("Error submitting form:", err);
      } finally {
        setIsLoading(false);
      }
    };

    postEarnSDollarForm();
  };

  return (
    <Box className="earn-s-dollar-form-container">
      <Typography className="title">
        <Trans i18nKey="s_dollar:form_title" />
      </Typography>
      <Typography
        className="description"
        dangerouslySetInnerHTML={{
          __html: t("s_dollar:form_description"),
        }}
      />
      {/* Invoice Number */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:invoice_number" />
        </Typography>
        <Controller
          name="invoice_number"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                inputRef={field.ref}
                name={field.name}
                fullWidth
                variant="outlined"
                error={!!errors.invoice_number}
              />
              {errors.invoice_number && (
                <Typography className="error-text">
                  {errors.invoice_number.message == "fieldRequired" && (
                    <Trans i18nKey="s_dollar:invoice_number_error" />
                  )}
                  {errors.invoice_number.message ==
                    "invoice_number is invalid" && (
                    <Trans i18nKey="s_dollar:invoice_number_must_start_with_hk_error" />
                  )}
                </Typography>
              )}
            </>
          )}
        />
      </Box>
      {/* Purchase Date */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:purchase_date" />
        </Typography>
        <Controller
          name="purchase_date"
          control={control}
          render={({ field }) => (
            <>
              <DatePicker
                inputRef={field.ref}
                name={field.name}
                open={openDatePicker}
                onOpen={() => setOpenDatePicker(true)}
                onClose={() => setOpenDatePicker(false)}
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => field.onChange(date)}
                minDate={dayjs().subtract(60, "day")} // 最早 60 日前
                maxDate={dayjs()} // 最遲 今日
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    className: "date-picker-field",
                    fullWidth: true,
                    error: !!errors.purchase_date,
                    onClick: () => setOpenDatePicker(true),
                    readOnly: true,
                  },
                }}
              />
              {errors.purchase_date && (
                <Typography className="error-text">
                  <Trans i18nKey="s_dollar:purchase_date_error" />
                </Typography>
              )}
            </>
          )}
        />
      </Box>
      {/* Purchase Price */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:purchase_price" />
        </Typography>
        <Controller
          name="purchase_price"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                inputRef={field.ref}
                name={field.name}
                fullWidth
                variant="outlined"
                error={!!errors.purchase_price}
              />
              {errors.purchase_price && (
                <Typography className="error-text">
                  <Trans i18nKey="s_dollar:purchase_price_error" />
                </Typography>
              )}
            </>
          )}
        />
      </Box>
      {/* Serial Number */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:serial_number" />
        </Typography>
        <Controller
          name="serial_number"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                inputRef={field.ref}
                name={field.name}
                fullWidth
                variant="outlined"
                error={!!errors.serial_number}
              />
              {errors.serial_number && (
                <Typography className="error-text">
                  <Trans i18nKey="s_dollar:serial_number_error" />
                </Typography>
              )}
            </>
          )}
        />
      </Box>
      {/* Purchase Shop */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:purchase_shop" />
        </Typography>
        <Controller
          name="purchase_shop"
          control={control}
          render={({ field }) => (
            <>
              <Select
                {...field}
                inputRef={field.ref}
                name={field.name}
                // 由原本 value=channel_id 改成 value=整個 object
                value={
                  shopOptions.find(
                    (x) =>
                      String(x.channel_id) === String(field.value?.channel_id)
                  ) || null
                }
                onChange={(e) => field.onChange(e.target.value)} // e.target.value 會係 item object
                sx={{ width: "100%", textAlign: "center" }}
                error={!!errors.purchase_shop}
                MenuProps={{ PaperProps: { sx: { maxHeight: "20vh" } } }}
              >
                {shopOptions.map((item) => (
                  <MenuItem
                    key={item.channel_id}
                    value={item}
                    sx={{ display: "block", textAlign: "center" }}
                  >
                    {isChinese ? item.title_hk : item.title_hk_en}
                  </MenuItem>
                ))}
              </Select>
              {errors.purchase_shop && (
                <Typography className="error-text">
                  <Trans i18nKey="s_dollar:purchase_shop_error" />
                </Typography>
              )}
            </>
          )}
        />
      </Box>{" "}
      {/* Invoice Image */}
      <Box className="field-container">
        <Typography>
          <span className="red">*</span>
          <Trans i18nKey="s_dollar:invoice_upload" />
        </Typography>

        <Controller
          name="invoice_image"
          control={control}
          render={({ field }) => (
            <Box sx={{ mt: 1 }}>
              {/* 隱藏 input：相片 */}
              <input
                type="file"
                id="upload-invoice-image"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  field.onChange(file);
                }}
              />

              {/* 隱藏 input：PDF */}
              <input
                type="file"
                id="upload-invoice-pdf"
                accept="application/pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  field.onChange(file);
                }}
              />
              {field.value && (
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", color: "text.secondary" }}
                >
                  <Trans i18nKey="s_dollar:selected" />
                  {field.value.name}
                </Typography>
              )}

              <InputAdornment position="end" sx={{ gap: 3 }}>
                {/* 相片掣 */}
                <IconButton
                  edge="end"
                  onClick={() =>
                    document.getElementById("upload-invoice-image")?.click()
                  }
                >
                  <span style={{ fontSize: 12 }}>JPEG / PNG </span>
                  <UploadFileIcon />
                </IconButton>{" "}
                {/* PDF 掣 */}
                <IconButton
                  edge="end"
                  onClick={() =>
                    document.getElementById("upload-invoice-pdf")?.click()
                  }
                >
                  <span style={{ fontSize: 12 }}>PDF</span>
                  <PictureAsPdfIcon />
                </IconButton>
              </InputAdornment>
            </Box>
          )}
        />
        {errors.invoice_image && (
          <Typography className="error-text">
            <Trans i18nKey="s_dollar:invoice_image_error" />
          </Typography>
        )}
      </Box>
      {/* Invoice Image 2 （非必填） */}
      <Box className="field-container">
        <Typography>
          {/* 呢個就唔落紅星啦 */}
          <Trans i18nKey="s_dollar:invoice_upload_2" />
        </Typography>

        <Controller
          name="invoice_image2"
          control={control}
          render={({ field }) => (
            <Box sx={{ mt: 1 }}>
              {/* 隱藏 input：相片 */}
              <input
                type="file"
                id="upload-invoice-image-2"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  field.onChange(file);
                }}
              />

              {/* 隱藏 input：PDF */}
              <input
                type="file"
                id="upload-invoice-pdf-2"
                accept="application/pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  field.onChange(file);
                }}
              />

              {field.value && (
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", color: "text.secondary" }}
                >
                  <Trans i18nKey="s_dollar:selected" />
                  {field.value.name}
                </Typography>
              )}

              <InputAdornment position="end" sx={{ gap: 3 }}>
                {/* 相片掣 */}
                <IconButton
                  edge="end"
                  onClick={() =>
                    document.getElementById("upload-invoice-image-2")?.click()
                  }
                >
                  <span style={{ fontSize: 12 }}>JPEG / PNG</span>
                  <UploadFileIcon />
                </IconButton>

                {/* PDF 掣 */}
                <IconButton
                  edge="end"
                  onClick={() =>
                    document.getElementById("upload-invoice-pdf-2")?.click()
                  }
                >
                  <span style={{ fontSize: 12 }}>PDF</span>
                  <PictureAsPdfIcon />
                </IconButton>
              </InputAdornment>
            </Box>
          )}
        />

        {errors.invoice_image2 && (
          <Typography className="error-text">
            {/* 你可以開多個 i18n key，例如 s_dollar:invoice_image2_error */}
            <Trans i18nKey="s_dollar:invoice_image_error" />
          </Typography>
        )}
      </Box>
      {/* Submit */}
      <Box className="button-container">
        <LoadingButton
          className="loading-button"
          onClick={handleSubmit(onSubmit, onError)}
          disabled={isLoading}
          loading={isLoading}
        >
          <Trans i18nKey="s_dollar:submit" />
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default EarnSDollarForm;
